import React, { useState, useEffect, useMemo } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { dispatch } from "../redux/store";
import Page from "./Page";
import TaskListContainer from "../components/tasks/TaskListContainer";
import { onDragEnd } from "../helpers/dnd";
import useWindowDimensions from "../helpers/window";
import AppFooter from "../components/footer/AppFooter";
import { Box } from "@mui/material";
import useTaskAPIClient from "../api/task/hooks/useTaskAPIClient";
import { setTaskTargeted as setTaskTargetedAction } from "../redux/actions/tasks";
import LoadingScreen from "../components/loading/LoadingScreen";
import useContentsHeight from "../hooks/useContentsHeight";
import TaskHelperDialog from "../components/tasks/taskCreator/TaskHelperDialog";
import useCreateTaskAction from "../hooks/tasks/useCreateTaskAction";
import TaskCreationPreferenceDialog from "../components/dialogs/TaskCreationPreferenceDialog";

const TasksPage = () => {
  const { setTaskTargeted } = useTaskAPIClient();
  const authenticated = useSelector((state) => state.user.authenticated);
  const [taskLists, setTaskLists] = useState({});
  const tasks = useSelector((state) => state.tasks.taskSet);

  const [isMobile, setIsMobile] = useState(false);
  const { width } = useWindowDimensions();
  const contentsHeight = useContentsHeight();
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Task Helper dialog state
  const [taskHelperOpen, setTaskHelperOpen] = useState(false);

  // Preference-aware task creation
  const {
    triggerCreateTask,
    showPreferenceDialog,
    setShowPreferenceDialog,
    handlePreferenceSelected,
  } = useCreateTaskAction("tasks", () => setTaskHelperOpen(true));

  const handleSearchToggle = () => {
    if (showSearch) {
      // When closing search, clear the search term
      setSearchTerm("");
    }
    setShowSearch(!showSearch);
  };

  //Effect to split tasks into appropriate lists
  useEffect(() => {
    let newTaskLists;
    if (!isMobile) {
      newTaskLists = {
        targets: {
          listId: "targets",
          name: "Targets",
          tasks: Object.values(tasks).filter((task) => task.targeted),
        },
        backlog: {
          listId: "backlog",
          name: "Tasks",
          tasks: Object.values(tasks).filter((task) => !task.targeted),
        },
      };
    } else {
      newTaskLists = {
        all: {
          listId: "all",
          name: "Tasks",
          tasks: Object.values(tasks),
        },
      };
    }

    setTaskLists(newTaskLists);
  }, [tasks, isMobile]);

  //Effect to determine view based on screen size
  useEffect(() => {
    if (width <= 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [width]);

  const setListsCallback = async (lists, movementDescription) => {
    const newTaskLists = {};
    lists.forEach((list) => {
      const listId = list.listId;
      newTaskLists[listId] = list;
    });

    const { taskId, from, to } = movementDescription;
    const { listId: fromListId } = from;
    const { listId: toListId, to: toIndex } = to;

    if (toListId === "targets" && fromListId !== "targets") {
      dispatch(setTaskTargetedAction({ taskId, targeted: true }));
      await setTaskTargeted(taskId, true);
    } else if (toListId !== "targets" && fromListId === "targets") {
      dispatch(setTaskTargetedAction({ taskId, targeted: false }));
      await setTaskTargeted(taskId, false);
    }

    setTaskLists(newTaskLists);
  };

  const targetsList = taskLists.targets;
  const backlogList = taskLists.backlog;

  const userHasTasks = useMemo(() => {
    if (taskLists) {
      return (
        targetsList?.tasks.length + backlogList?.tasks.length > 0 ||
        taskLists.all?.tasks.length > 0
      );
    }
  }, [taskLists]);

  if (!authenticated) {
    return <LoadingScreen />;
  }

  return (
    <Page className="tasks-page" pageKey="tasks">
      <Box
        className="tasks-page__dnd-container"
        sx={{ height: contentsHeight }}
      >
        <DragDropContext
          onDragEnd={onDragEnd(Object.values(taskLists), setListsCallback)}
        >
          {isMobile ? (
            <div className="tasks-page__primary-list-container scroller">
              <TaskListContainer
                {...taskLists.all}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showSearch={showSearch}
                onSearchToggle={handleSearchToggle}
                userHasTasks={userHasTasks}
                onOpenTaskHelper={triggerCreateTask}
              />
            </div>
          ) : (
            <>
              <div className="tasks-page__primary-list-container scroller">
                {targetsList && (
                  <TaskListContainer
                    {...targetsList}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showSearch={showSearch}
                    onSearchToggle={handleSearchToggle}
                    userHasTasks={userHasTasks}
                    onOpenTaskHelper={triggerCreateTask}
                  />
                )}
              </div>
              <div className="tasks-page__secondary-lists-container scroller">
                {backlogList && (
                  <TaskListContainer
                    {...backlogList}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showSearch={showSearch}
                    onSearchToggle={handleSearchToggle}
                    userHasTasks={userHasTasks}
                    onOpenTaskHelper={triggerCreateTask}
                  />
                )}
              </div>
            </>
          )}
        </DragDropContext>
      </Box>
      <TaskHelperDialog
        open={taskHelperOpen}
        onClose={() => setTaskHelperOpen(false)}
      />
      <TaskCreationPreferenceDialog
        open={showPreferenceDialog}
        onClose={() => setShowPreferenceDialog(false)}
        onSelected={handlePreferenceSelected}
      />
      <AppFooter page="tasks" onOpenTaskHelper={() => setTaskHelperOpen(true)} />
    </Page>
  );
};

export default TasksPage;
